/**
 * Secure vault plugin — NFR-NAT-002 / SEC-STOR-*.
 *
 * Native (iOS Keychain / Android Keystore) must be wired via Capacitor plugin
 * registration. Until the native binary plugins ship, this module provides:
 * 1. A stable plugin interface for Keychain/Keystore-backed secrets
 * 2. A web/dev fallback that NEVER stores the vault wrapping key in IndexedDB
 *    (Preferences / localStorage only for the Argon2id-wrapped blob)
 * 3. Clear capability reporting so UI cannot claim hardware-backed storage falsely
 */

export type SecureVaultBackend = "keychain" | "keystore" | "preferences" | "memory";

export interface SecureVaultCapabilities {
  backend: SecureVaultBackend;
  hardwareBacked: boolean;
  biometricUnlock: boolean;
  /** True only when Keychain/Keystore bridge is registered. */
  productionReady: boolean;
}

export interface SecureVaultPlugin {
  capabilities(): Promise<SecureVaultCapabilities>;
  set(options: { key: string; value: string }): Promise<void>;
  get(options: { key: string }): Promise<{ value: string | null }>;
  remove(options: { key: string }): Promise<void>;
}

const MEMORY = new Map<string, string>();

function detectNativeBridge(): SecureVaultPlugin | null {
  const w = globalThis as unknown as {
    Capacitor?: { Plugins?: { VetoSecureVault?: SecureVaultPlugin } };
  };
  return w.Capacitor?.Plugins?.VetoSecureVault ?? null;
}

/** Preferences-backed fallback (PWA / Capacitor web). Not hardware-backed. */
export const SecureVaultWeb: SecureVaultPlugin = {
  async capabilities() {
    return {
      backend: "preferences",
      hardwareBacked: false,
      biometricUnlock: false,
      productionReady: false,
    };
  },
  async set({ key, value }) {
    try {
      const { Preferences } = await import("@capacitor/preferences");
      await Preferences.set({ key: `veto.secure.${key}`, value });
    } catch {
      MEMORY.set(key, value);
      try {
        localStorage.setItem(`veto.secure.${key}`, value);
      } catch {
        /* private mode */
      }
    }
  },
  async get({ key }) {
    try {
      const { Preferences } = await import("@capacitor/preferences");
      const { value } = await Preferences.get({ key: `veto.secure.${key}` });
      return { value };
    } catch {
      const fromMem = MEMORY.get(key);
      if (fromMem != null) return { value: fromMem };
      try {
        return { value: localStorage.getItem(`veto.secure.${key}`) };
      } catch {
        return { value: null };
      }
    }
  },
  async remove({ key }) {
    MEMORY.delete(key);
    try {
      const { Preferences } = await import("@capacitor/preferences");
      await Preferences.remove({ key: `veto.secure.${key}` });
    } catch {
      try {
        localStorage.removeItem(`veto.secure.${key}`);
      } catch {
        /* ignore */
      }
    }
  },
};

/** Resolve platform plugin: native Keychain/Keystore when present, else web fallback. */
export async function createSecureVault(): Promise<SecureVaultPlugin> {
  const native = detectNativeBridge();
  if (native) return native;
  return SecureVaultWeb;
}

export async function reportSecureVaultCapabilities(): Promise<SecureVaultCapabilities> {
  const vault = await createSecureVault();
  return vault.capabilities();
}
